/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import {
    LogoVariant,
    MpbLogo,
    MpbLogoSize,
} from '@mpb-com/feather/components/atoms/mpb-logo';
import { animated, useSpring } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import NextLink from 'next/link';
import React, {
    Fragment,
    FunctionComponent,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import { getAccountLinks } from '@toucan/common/services/navigation/helpers/navigation';
import { NavigationLinks } from '@toucan/common/services/navigation/types';

import MenuIcon from '@toucan/client/global/icons/menu.svg';
import ShoppingBasketIcon from '@toucan/client/global/icons/shopping-basket-eu.svg';
import ShoppingCartIcon from '@toucan/client/global/icons/shopping-cart-us.svg';
import AccountIcon from '@toucan/client/global/icons/user.svg';

import {
    ApplicationContext,
    MediaQuery,
    ModalSize,
    useMediaQuery,
} from '@toucan/client/global/application';
import { headerSizes } from '@toucan/client/global/layout';
import { MarketCode, MarketContext } from '@toucan/client/global/market';
import { WrappedLink, isBrowser } from '@toucan/client/global/nextjs';

import {
    ConstrainedWrapper,
    ConstrainedWrapperGutter,
    ConstrainedWrapperType,
} from '@toucan/client/shared/components/constrained-wrapper';
import { LinkColor, LinkSize } from '@toucan/client/shared/components/link';
import { BasketContext } from '@toucan/client/shared/context/basket';
import { useMounted } from '@toucan/client/shared/hooks/use-mounted';
import { useScroll } from '@toucan/client/shared/hooks/use-scroll';
import { useWindowResizeCallback } from '@toucan/client/shared/hooks/use-window-resize';

import { LoginForm } from '@toucan/client/blocks/components/login-form';

import { BasketButton, BasketButtonProps } from './components/basket-button';
import { NlxBetaButton } from './components/nlx-beta-button';
import { PopoutMenu } from './components/popout-menu';
import { PopoutMiniBasket } from './components/popout-mini-basket';
import { SearchBar, SearchBarLocation } from './components/search-bar';
import { StickyHeader } from './components/sticky-header';
import headerLockSubject, {
    HeaderLock,
    HeaderLockObserver,
    HeaderObserverState,
} from './header-lock-subject';
import { headerPaddingVariations, styles } from './header.styles';
import { useMenu } from './hooks/use-menu';

export type HeaderProps = {
    /**
     * Whether the user is logged in or not
     *
     * @default false
     */
    loggedIn?: boolean;

    /**
     * The number of items in the users basket
     *
     * @default 0
     */
    basketCount?: number;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
    navigationLinks: NavigationLinks;
};

export type StyledHeaderProps = HeaderProps & {
    isSmallHeader: boolean;
    isSearchBarOpen: boolean;
    showAdditionalHeader: boolean;
};

const StyledHeader = styled(animated.header).withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})<StyledHeaderProps>`
    ${styles}
`;

const StyledContentWrapper = styled(animated.div)``;

/**
 * Converts a padding variation to a float value of Y padding
 * @param variation A padding variation in the format '2.4rem 0'
 */
function convertPaddingToYFloat(variation: string) {
    return parseFloat(variation.split(' ')[0]);
}

export const Header: FunctionComponent<HeaderProps> = ({
    loggedIn = false,
    navigationLinks,
    ...otherProps
}: HeaderProps) => {
    const { t } = useTranslation();
    const { menu, setMenuVisible } = useMenu(navigationLinks);
    const isMounted = useMounted();
    const windowRef = useRef(isBrowser() ? window : null);
    const previousYOffset = useRef(0);
    const updateLocksBasedOnScroll = useRef<() => void>(() => {});

    const isDesktop = useMediaQuery(MediaQuery.DESKTOP);
    const isLargeTablet = useMediaQuery(MediaQuery.LARGE_TABLET);
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);

    const [{ y, p }, setHeaderStyleProps] = useSpring(() => ({
        y: 0,
        p: convertPaddingToYFloat(
            headerPaddingVariations[MediaQuery.DESKTOP].normal
        ),
    }));

    const [{ y: additionalHeaderY }, setAdditionalHeaderStyleProps] = useSpring(
        () => ({
            y: -110,
        })
    );

    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState<boolean>(false);
    const [isSearchBarOpen, setIsSearchBarOpen] = useState<boolean>(false);

    const [{ isVisible, isSmallHeader }, setHeaderLockState] =
        useState<HeaderObserverState>(headerLockSubject.getHeaderState());

    const {
        additionalHeaderDisplayBoundary,
        setModalVisible,
        setModalContent,
        setModalSize,
        setBlackoutVisible,
    } = useContext(ApplicationContext);
    const { activeMarket } = useContext(MarketContext);
    const { basket, miniBasket, setMiniBasketVisible } =
        useContext(BasketContext);
    const [basketCount, setBasketCount] = useState<number>(basket.items.length);
    const accountMenuLinks = getAccountLinks(activeMarket, t);

    const [showAdditionalHeader, setAdditionalHeaderVisibilityState] =
        useState(false);

    const headerRef = useRef<HTMLElement>(null);

    function handleOpenMenu() {
        setMenuVisible(true);
    }

    function handleOpenLogin() {
        setModalSize(ModalSize.SMALL);
        setModalContent(
            <LoginForm onLogInSuccess={() => setModalVisible(false)} />
        );
        setModalVisible(true);
    }

    const getHeaderHeight = () => {
        let headerHeight = headerSizes[MediaQuery.DESKTOP].full;
        if (isSmallHeader) {
            headerHeight = headerSizes[MediaQuery.DESKTOP].collapsed;
        }
        if (isSmallTablet) {
            headerHeight = headerSizes[MediaQuery.SMALL_TABLET];
        } else if (isLargeTablet) {
            headerHeight = headerSizes[MediaQuery.LARGE_TABLET];
        }

        return headerHeight;
    };

    updateLocksBasedOnScroll.current = () => {
        const headerHeight = getHeaderHeight();

        const yOffset = isDesktop
            ? headerSizes[MediaQuery.DESKTOP].full -
              headerSizes[MediaQuery.DESKTOP].collapsed
            : 0; // No difference in header heights on smaller devices

        const { pageYOffset } = window;
        const previousY = previousYOffset.current;

        previousYOffset.current = pageYOffset;

        if (pageYOffset <= yOffset) {
            headerLockSubject.addLock(HeaderLock.TOP);
        } else {
            headerLockSubject.removeLock(HeaderLock.TOP);
        }

        if (pageYOffset < headerHeight * 3) {
            headerLockSubject.addLock(HeaderLock.INITIAL);
        } else {
            headerLockSubject.removeLock(HeaderLock.INITIAL);
        }

        if (previousY >= pageYOffset) {
            headerLockSubject.addLock(HeaderLock.SCROLL_UP);
        } else {
            headerLockSubject.removeLock(HeaderLock.SCROLL_UP);
        }

        const { start, end } = additionalHeaderDisplayBoundary;

        const isWithinStartBoundary = start ? start <= pageYOffset : true;
        const isWithinEndBoundary = end ? end >= pageYOffset : true;

        let withinBoundary = false;

        const hasStartBoundary = Boolean(start);
        const hasEndBoundary = Boolean(end);

        if (hasStartBoundary && hasEndBoundary) {
            withinBoundary = isWithinStartBoundary && isWithinEndBoundary;
        } else if (hasStartBoundary && !hasEndBoundary) {
            withinBoundary = isWithinStartBoundary;
        } else if (!hasStartBoundary && hasEndBoundary) {
            withinBoundary = isWithinEndBoundary;
        }

        if (withinBoundary) {
            setAdditionalHeaderVisibilityState(true);
        } else {
            setAdditionalHeaderVisibilityState(false);
        }
    };

    const accountButtonProps = {
        size: IconSize.L,
        variant: IconVariant.ACTIONABLE_SECONDARY,
        'data-testid': 'header__login-button',
        'aria-label': t('Account'),
    };

    const accountMenuButton = loggedIn ? (
        <PopoutMenu
            isOpen={isAccountMenuOpen}
            trigger={(id, ref) => {
                return (
                    <Icon
                        {...accountButtonProps}
                        variant={IconVariant.DECORATIVE}
                        className="logged-in-icon"
                        aria-controls={id}
                    >
                        <AccountIcon />
                    </Icon>
                );
            }}
            onOpen={() => {
                // Don't allow account menu to be open when mini basket is visible
                if (miniBasket.visible) {
                    return;
                }
                setIsAccountMenuOpen(true);
            }}
            onClose={() => setIsAccountMenuOpen(false)}
            menuItems={accountMenuLinks}
            className="header-account-menu account-menu-button"
            data-testid="header__account-menu"
        />
    ) : (
        <Icon
            {...accountButtonProps}
            onClick={handleOpenLogin}
            className="account-menu-button"
        >
            <AccountIcon />
        </Icon>
    );

    /**
     * Renders the basket button for the header
     *
     * @param props Any button props that should be overwritten
     */
    function renderBasketButton(props: Partial<BasketButtonProps>) {
        return (
            <BasketButton
                numItemsInBasket={basket?.items?.length}
                className="basket"
                onClick={() => {
                    setIsAccountMenuOpen(false);
                    setMiniBasketVisible(!miniBasket.visible);
                }}
                {...props}
            >
                {activeMarket.code === MarketCode.EN_US ? (
                    <ShoppingCartIcon />
                ) : (
                    <ShoppingBasketIcon />
                )}
            </BasketButton>
        );
    }

    const basketSlug = t('basket', { ns: 'urls' });
    const basketUrl = `/${activeMarket.slug}/s/${basketSlug}`;

    const miniBasketButton = isDesktop ? (
        <PopoutMiniBasket
            isOpen={miniBasket.visible}
            trigger={(id, ref) =>
                renderBasketButton({
                    buttonRef: ref,
                    'data-testid': 'header__basket-button',
                })
            }
            onClose={() => setMiniBasketVisible(false)}
            className="header-popout-mini-basket"
            data-testid="header__popout-mini-basket"
        />
    ) : (
        <NextLink
            prefetch={false}
            href="/basket"
            as={basketUrl}
            passHref={true}
            legacyBehavior
        >
            {renderBasketButton({
                'data-testid': 'header__mobile-basket-button',
            })}
        </NextLink>
    );

    /**
     * Handles opening the mini basket when the
     * basket item length changes
     */
    useEffect(() => {
        if (basket.items.length > basketCount) {
            setMiniBasketVisible(true);
            setBasketCount(basket.items.length);
        }
    }, [basket.items.length]);

    /**
     * Attaches a listener to the headerLockSubject.
     * Used to update header state based on locks
     */
    useEffect(() => {
        const handler: HeaderLockObserver = (state) => {
            setHeaderLockState(state);
        };

        headerLockSubject.attach(handler);
        updateLocksBasedOnScroll.current();

        return () => headerLockSubject.detach(handler);
    }, []);

    /**
     * Handles animating the header when it should open/close
     */
    useEffect(() => {
        const headerHeight = getHeaderHeight();
        const headerRem = headerHeight / 10;

        const y = isVisible ? 0 : -headerRem;
        const p = isSmallHeader
            ? convertPaddingToYFloat(
                  headerPaddingVariations[MediaQuery.DESKTOP].sticky
              )
            : convertPaddingToYFloat(
                  headerPaddingVariations[MediaQuery.DESKTOP].normal
              );

        setHeaderStyleProps.start({
            y,
            p,
        });

        const headerHeightCssVariable = isVisible ? headerHeight : 0;
        document.documentElement.style.setProperty(
            '--header-height',
            `${headerHeightCssVariable}px`
        );
    }, [isVisible, isSmallHeader]);

    useEffect(() => {
        const y = showAdditionalHeader ? 0 : -110;
        setAdditionalHeaderStyleProps.start({ y });
    }, [showAdditionalHeader]);

    /**
     * Adds & removes HeaderLocks based on the scroll position
     */
    useScroll(windowRef, () => {
        updateLocksBasedOnScroll.current();
    });

    useWindowResizeCallback(() => {
        updateLocksBasedOnScroll.current();
    });

    return (
        <Fragment>
            <StyledHeader
                loggedIn={loggedIn}
                isSmallHeader={isSmallHeader}
                showAdditionalHeader={showAdditionalHeader}
                isSearchBarOpen={isMounted() ? isSearchBarOpen : false}
                style={{
                    transform: y.to((y: number) => `translateY(${y}rem)`),
                }}
                ref={headerRef}
                navigationLinks={navigationLinks}
                {...otherProps}
            >
                <div className="header-content-wrapper">
                    <ConstrainedWrapper
                        type={ConstrainedWrapperType.PADDING}
                        gutter={ConstrainedWrapperGutter.SMALL_GUTTER}
                    >
                        <StyledContentWrapper
                            className="header-content"
                            style={{
                                padding: isDesktop
                                    ? p.to((p: number) => `${p}rem 0`)
                                    : undefined,
                            }}
                        >
                            <div className="shop-actions">
                                <Icon
                                    variant={IconVariant.ACTIONABLE_SECONDARY}
                                    size={IconSize.L}
                                    className="menu"
                                    onClick={handleOpenMenu}
                                    data-testid="header__menu-button"
                                    aria-label={t('Menu')}
                                >
                                    <MenuIcon />
                                </Icon>

                                <WrappedLink
                                    className="logo-link"
                                    prefetch={false}
                                    as={`/${activeMarket.slug}`}
                                    href="/"
                                    legacyBehavior
                                >
                                    <MpbLogo
                                        className="logo"
                                        data-testid="header__logo"
                                        logoSize={MpbLogoSize.LARGE}
                                        variant={LogoVariant.SECONDARY}
                                    />
                                </WrappedLink>
                                <div className="search-bar-wrapper">
                                    <SearchBar
                                        isOpen={isSearchBarOpen}
                                        allowClear={true}
                                        stealFocus={false}
                                        constrainWidth={false}
                                        location={SearchBarLocation.SITEWIDE}
                                        onFocus={() => {
                                            setIsSearchBarOpen(true);
                                            setBlackoutVisible(true);
                                        }}
                                        onCollapse={() => {
                                            setIsSearchBarOpen(false);
                                            setBlackoutVisible(false);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="user-actions">
                                <NlxBetaButton />
                                <div className="sell-and-trade-container">
                                    <span>{t('trade in your kit to MPB')}</span>
                                    <WrappedLink
                                        prefetch={false}
                                        href="/sell-form"
                                        as={`/${activeMarket.slug}/${t(
                                            'sell-or-trade',
                                            {
                                                ns: 'urls',
                                            }
                                        )}`}
                                        passHref={true}
                                        size={LinkSize.LARGE}
                                        linkColor={LinkColor.LIGHT}
                                        data-testid="header__sell-button"
                                        aria-label={t(
                                            'Start selling or trading'
                                        )}
                                    >
                                        {t('Start selling or trading')}
                                        &nbsp;&gt;
                                    </WrappedLink>
                                </div>
                                <div className="user-actions-icons">
                                    {accountMenuButton}
                                    {miniBasketButton}
                                </div>
                            </div>
                        </StyledContentWrapper>
                    </ConstrainedWrapper>
                </div>
                <StickyHeader
                    style={{
                        transform: additionalHeaderY.to(
                            (y: number) => `translateY(${y}%)`
                        ),
                        borderWidth: showAdditionalHeader ? 1 : 0,
                    }}
                />
            </StyledHeader>
            <span aria-hidden={true} className="header-shim" />
            {menu}
        </Fragment>
    );
};
